import { UserRole } from '../value-objects/UserRole';
import { Email } from '../value-objects/Email';
import { UserPreferences } from '../value-objects/UserPreferences';

/**
 * User Entity
 * Represents an authenticated user in the system
 *
 * DB Table: Users
 * - id (PK)
 * - workspace_id (FK, nullable for ADMIN)
 * - google_id
 * - email
 * - password_hash
 * - full_name
 * - role (ENUM: DIRECTOR, TEACHER, STUDENT, ADMIN)
 */
export interface UserProps {
  id: string;
  workspaceId: string | null;
  googleId: string | null;
  email: Email;
  passwordHash: string | null;
  fullName: string;
  role: UserRole;
  avatar?: string;
  preferences?: UserPreferences;
  mustChangePassword?: boolean;
  createdAt?: Date;
  updatedAt?: Date;
  deletedAt?: Date;
}

export class User {
  private readonly props: UserProps;

  constructor(props: UserProps) {
    this.props = props;
  }

  get id(): string {
    return this.props.id;
  }

  get workspaceId(): string | null {
    return this.props.workspaceId;
  }

  get googleId(): string | null {
    return this.props.googleId;
  }

  get email(): Email {
    return this.props.email;
  }

  get passwordHash(): string | null {
    return this.props.passwordHash;
  }

  get fullName(): string {
    return this.props.fullName;
  }

  /**
   * Alias for fullName for backwards compatibility
   */
  get name(): string {
    return this.props.fullName;
  }

  get role(): UserRole {
    return this.props.role;
  }

  get avatar(): string | undefined {
    return this.props.avatar;
  }

  get createdAt(): Date | undefined {
    return this.props.createdAt;
  }

  get updatedAt(): Date | undefined {
    return this.props.updatedAt;
  }

  get deletedAt(): Date | undefined {
    return this.props.deletedAt;
  }

  /**
   * Get user preferences (returns default if not set)
   */
  get preferences(): UserPreferences {
    return this.props.preferences ?? UserPreferences.create();
  }

  /**
   * Check if user must change password on first login
   */
  get mustChangePassword(): boolean {
    return this.props.mustChangePassword ?? false;
  }

  /**
   * Check if user authenticated via Google OAuth
   */
  isGoogleUser(): boolean {
    return this.props.googleId !== null;
  }

  /**
   * Check if user has a password set
   */
  hasPassword(): boolean {
    return this.props.passwordHash !== null;
  }

  isDirector(): boolean {
    return this.role === 'director';
  }

  isTeacher(): boolean {
    return this.role === 'teacher';
  }

  isStudent(): boolean {
    return this.role === 'student';
  }

  isAdmin(): boolean {
    return this.role === 'admin';
  }

  /**
   * @deprecated Use isTeacher() instead
   */
  isProfesor(): boolean {
    return this.isTeacher();
  }

  /**
   * @deprecated Use isStudent() instead
   */
  isAlumno(): boolean {
    return this.isStudent();
  }

  /**
   * Determines if user can access a specific module
   */
  canAccessModule(moduleId: string): boolean {
    const modulePermissions: Record<string, UserRole[]> = {
      'dashboard': ['director', 'teacher', 'student', 'admin'],
      'workspaces': ['admin'],
      'estructura': ['director'],
      'mis-materias': ['teacher'],
      'inscripciones': ['director', 'student'],
      'horarios': ['director', 'teacher', 'student'],
      'lms': ['director', 'teacher', 'student'],
      'notificaciones': ['director'],
      'estadisticas': ['director'],
    };

    return modulePermissions[moduleId]?.includes(this.role) ?? false;
  }

  /**
   * Check if user can manage other users
   */
  canManageUsers(): boolean {
    return this.isDirector();
  }

  /**
   * Check if user can create courses
   */
  canCreateCourses(): boolean {
    return this.isDirector();
  }

  /**
   * Check if user can grade students
   */
  canGradeStudents(): boolean {
    return this.isDirector() || this.isTeacher();
  }

  /**
   * Check if user belongs to a specific workspace
   * Note: ADMIN users (workspaceId = null) don't belong to any specific workspace
   */
  belongsToWorkspace(workspaceId: string): boolean {
    return this.props.workspaceId !== null && this.props.workspaceId === workspaceId;
  }

  /**
   * Check if user is soft-deleted
   */
  isDeleted(): boolean {
    return this.props.deletedAt !== undefined;
  }

  /**
   * Check if user is active (not deleted)
   */
  isActive(): boolean {
    return !this.isDeleted();
  }

  toPlainObject(): UserProps {
    return { ...this.props };
  }
}
